/**
 * MonitorTabs — tab navigation shared by every per-chain monitor.
 *
 * Renders a horizontal pill-style row with one button per tab. The
 * active tab paints with the chain gradient; the others stay
 * transparent with muted text. Click a tab → caller's `onChange`
 * fires.
 *
 * V2.I.5.3: full ARIA tabs pattern — only the active tab is in the
 * tab order, ←/→ arrows navigate between tabs (with wraparound),
 * Home/End jump to first/last. `aria-controls` ties each tab to its
 * panel via `panelIdPrefix` (caller can render the panel with the
 * matching `id`).
 */

import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import type { NetworkColors } from './types';

export interface MonitorTab<Id extends string = string> {
  id: Id;
  label: string;
  icon?: ReactNode;
}

interface MonitorTabsProps<Id extends string = string> {
  tabs: MonitorTab<Id>[];
  activeTab: Id;
  onChange: (id: Id) => void;
  colors: NetworkColors;
  /** Optional aria-label for the tablist landmark. */
  ariaLabel?: string;
  /**
   * Optional panel id prefix. When set, each tab gets
   * `aria-controls="<prefix>-<tabId>"`. Caller is expected to render
   * the matching panel with `id` and `role="tabpanel"`.
   */
  panelIdPrefix?: string;
}

export function MonitorTabs<Id extends string = string>({
  tabs,
  activeTab,
  onChange,
  colors,
  ariaLabel,
  panelIdPrefix,
}: MonitorTabsProps<Id>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = (idx: number) => {
    const wrapped = ((idx % tabs.length) + tabs.length) % tabs.length;
    const node = refs.current[wrapped];
    if (node) {
      node.focus();
      onChange(tabs[wrapped].id);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        focusTab(idx + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        focusTab(idx - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(tabs.length - 1);
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      style={{
        display: 'flex',
        gap: '4px',
        padding: '4px',
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '14px',
        width: 'fit-content',
        marginBottom: '24px',
      }}
    >
      {tabs.map((tab, idx) => {
        const isActive = tab.id === activeTab;
        const tabId = panelIdPrefix ? `${panelIdPrefix}-tab-${tab.id}` : undefined;
        const panelId = panelIdPrefix ? `${panelIdPrefix}-${tab.id}` : undefined;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            type="button"
            role="tab"
            id={tabId}
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            style={{
              padding: '10px 18px',
              borderRadius: '10px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
              background: isActive ? colors.gradient : 'transparent',
              color: isActive ? 'white' : 'var(--muted)',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
